import React, { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import {
  theme,
  generateCSSCustomProperties,
  getColor,
  getFont,
} from '../lib/theme';
import type { ThemeColors } from '../lib/theme';

// Theme context type
interface ThemeContextType {
  theme: typeof theme;
  getColor: (path: string) => string;
  getFont: (fontName?: 'primary') => string;
  currentTheme: 'light' | 'dark';
  toggleTheme: () => void;
  colors: ThemeColors;
}

// Create theme context
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Theme provider props
interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: 'light' | 'dark';
}

// Theme provider component
export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light',
}) => {
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark'>(
    defaultTheme
  );

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    const properties = generateCSSCustomProperties();

    // Apply CSS custom properties to root
    Object.entries(properties).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    // Apply theme class to body
    document.body.classList.remove('light', 'dark');
    document.body.classList.add(currentTheme);

    // Set data attribute for theme
    document.documentElement.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  // Toggle between light and dark theme
  const toggleTheme = () => {
    setCurrentTheme(prev => (prev === 'light' ? 'dark' : 'light'));
  };

  // Create context value
  const contextValue: ThemeContextType = {
    theme,
    getColor,
    getFont,
    currentTheme,
    toggleTheme,
    colors: theme.colors,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use theme context
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};

// Utility hook for color access
export const useColors = () => {
  const { colors, getColor } = useTheme();
  return { colors, getColor };
};

// Utility hook for font access
export const useFonts = () => {
  const { getFont } = useTheme();
  return { getFont };
};

// HOC for theme-aware components
export const withTheme = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const ThemedComponent = (props: P) => {
    const themeContext = useTheme();
    return <Component {...props} theme={themeContext} />;
  };

  ThemedComponent.displayName = `withTheme(${Component.displayName || Component.name})`;
  return ThemedComponent;
};

export default ThemeProvider;
